import { useMemo } from "react"
import { observer } from "mobx-react-lite"
import { Address } from "viem"
import { useConfig } from "wagmi"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { claimAllNftBribesInteraction } from "@/domains/interactions/claim-all-nft-bribes/claim-all-nft-bribes-interaction"
import { cn, dollars } from "@/domains/library"
import { LockRewardsPageHeader } from "@/domains/lock-rewards/components/page-header"
import { GroupVotingRewardsBy } from "@/domains/old-voting-rewards/components/voting-rewards-tabs"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { ClaimEverythingByNftFormat } from "../strategies/selection/lock-reward-selection-strategy"
import { LockRewardStoreNftGroup } from "../types"
import { BigTokenGroupCard } from "./big-token-group-card"

export const LockRewardsGroupedByNft = observer(
  ({ groupBy, setGroupBy }: { groupBy: GroupVotingRewardsBy; setGroupBy: (groupBy: GroupVotingRewardsBy) => void }) => {
    const scaleApp = useScaleApplication()
    const config = useConfig()
    const emitter = useEventEmitter()
    const chainConfig = useChainConfig()
    const strategy = scaleApp.LockRewards.Strategies.NftGrouping
    const allTokens = scaleApp.LockRewards.Repository.getAll()
    const groupedNfts = strategy.group(allTokens)
    const selectionStrategy = scaleApp.LockRewards.Strategies.NftSelection
    const claimFormat = useMemo(() => selectionStrategy.toClaimFormat(allTokens), [selectionStrategy, allTokens])
    const chainId = chainConfig.chainId

    const selectedRewardsValueUSD = useMemo(() => {
      return selectionStrategy.filteredSelected(allTokens).reduce((acc, item) => acc + item.valueUSD, 0)
    }, [selectionStrategy, allTokens])

    const claimAllHandler = async () => {
      for (let i = 0; i < claimFormat.length; i++) {
        const item = claimFormat[i] as ClaimEverythingByNftFormat
        if (!item) continue

        const result = await claimAllNftBribesInteraction({
          account: scaleApp.Wallet.Connection.connectedAddress?.toViemAddress() as Address,
          chainId,
          config,
          voterAddress: chainConfig.equalizer.contracts.voter,
          bribes: item.bribes as Address[],
          bribeTokens: item.bribeTokens as Array<Array<Address>>,
          nft: { id: item.nftId },
          emitter,
        })

        if (result === false) {
          break
        }
      }
    }

    return (
      <>
        <LockRewardsPageHeader
          groupBy={groupBy}
          numberOfLocksWithRewards={claimFormat.length}
          setGroupBy={setGroupBy}
          totalRewardsValueUSD={selectedRewardsValueUSD}
          claimAllHandler={claimAllHandler}
        />
        <div className="flex flex-col gap-4">
          {Array.from(groupedNfts.entries()).map(([nftId, nftGroup]) => {
            const isSelected = selectionStrategy.has(nftId)
            return (
              <div key={nftId} className="max-w-6xl">
                <NftCardHeader
                  nftId={nftId}
                  nftGroup={nftGroup}
                  isSelected={isSelected}
                  onToggleSelect={() => selectionStrategy.toggle(nftId)}
                />
                <div key={nftId} className="py-3 md:px-4">
                  <div className={cn("grid grid-cols-[repeat(auto-fill,minmax(158px,1fr))] gap-2")}>
                    {Array.from(nftGroup.tokens.entries()).map(([tokenAddress, tokenGroup]) => (
                      <BigTokenGroupCard
                        key={tokenAddress}
                        tokenGroup={tokenGroup}
                        isSelected={false}
                        onToggleSelect={() => {}}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </>
    )
  },
)

const NftCardHeader = observer(
  ({
    nftId,
    nftGroup,
    isSelected,
    onToggleSelect,
  }: {
    nftId: number
    nftGroup: LockRewardStoreNftGroup
    isSelected: boolean
    onToggleSelect: () => void
  }) => {
    return (
      <div
        className={cn(
          "flex items-center justify-between rounded-xl px-4 py-2",
          "cursor-pointer",
          isSelected ? "bg-green-700 hover:bg-green-600/80" : "bg-p400 hover:bg-p400-light",
        )}
        onClick={onToggleSelect}
      >
        <span className="">veSCALE #{nftId}</span>
        <span className={cn("ml-2", isSelected ? "text-green-100" : "text-t200")}>
          {dollars(nftGroup.totalValueUSD, 2)}
        </span>
      </div>
    )
  },
)
